import { forwardRef } from 'react';
import { View } from 'react-native';

import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownItemText,
  DropdownPortal,
  DropdownTrigger,
} from '@squire/ui';

import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { styles } from './styles';
import type { CategorySelectProps } from './types';

/** Sentinel value for services that have no category. */
export const UNCATEGORIZED_CATEGORY_ID = 'uncategorized';

const ALL_CATEGORIES_VALUE = 'all';

const CategorySelect = forwardRef<View, CategorySelectProps>(
  (
    {
      categories,
      selectedCategoryId,
      onCategoryChange,
      placeholder,
      testID,
      style,
      ...props
    },
    ref,
  ) => {
    const { t } = useScreenTranslations('CategorySelect');

    // Use translated placeholder if none provided
    const displayPlaceholder = placeholder || t('placeholder');
    // Convert category ID to dropdown value ("all" when nothing is selected)
    const dropdownValue = selectedCategoryId || ALL_CATEGORIES_VALUE;

    // Handle dropdown value change
    const handleDropdownChange = (value: string) => {
      const categoryId = value === ALL_CATEGORIES_VALUE ? undefined : value;
      onCategoryChange(categoryId);
    };

    // Resolve the label shown on the trigger for the current value. This must
    // be derived here rather than relying on the dropdown items rendering,
    // because the items live inside a Modal that is closed by default and
    // therefore never register their labels until first opened.
    const renderSelectedOption = (value: string) => {
      let label = t('all');
      if (value === UNCATEGORIZED_CATEGORY_ID) {
        label = t('uncategorized');
      } else if (value !== ALL_CATEGORIES_VALUE) {
        label = categories.find((c) => c.id === value)?.name ?? label;
      }

      return (
        <DropdownItemText style={styles.dropdownItemText}>
          {label}
        </DropdownItemText>
      );
    };

    return (
      <View ref={ref} style={style} {...props}>
        <Dropdown
          value={dropdownValue}
          onChange={handleDropdownChange}
          testID={testID}
        >
          <DropdownTrigger
            role="button"
            placeholder={displayPlaceholder}
            renderSelectedOption={renderSelectedOption}
            testID={`${testID}-dropdown-trigger`}
            style={styles.dropdownTrigger}
          />

          <DropdownPortal>
            <DropdownContent
              scrollEnabled
              testID={`${testID}-dropdown-content`}
              style={styles.dropdownContent}
            >
              {/* "All" option */}
              <DropdownItem value={ALL_CATEGORIES_VALUE}>
                <DropdownItemText style={styles.dropdownItemText}>
                  {t('all')}
                </DropdownItemText>
              </DropdownItem>

              {/* Category options */}
              {categories.map((category) => (
                <DropdownItem key={category.id} value={category.id}>
                  <DropdownItemText style={styles.dropdownItemText}>
                    {category.name}
                  </DropdownItemText>
                </DropdownItem>
              ))}

              {/* "Uncategorized" option */}
              <DropdownItem value={UNCATEGORIZED_CATEGORY_ID}>
                <DropdownItemText style={styles.dropdownItemText}>
                  {t('uncategorized')}
                </DropdownItemText>
              </DropdownItem>
            </DropdownContent>
          </DropdownPortal>
        </Dropdown>
      </View>
    );
  },
);

CategorySelect.displayName = 'CategorySelect';
export { CategorySelect };
